import React from 'react';
import PropTypes from 'prop-types';
import { Text } from 'office-ui-fabric-react/lib/Text';
import flatten from 'lodash/flatten';
import get from 'lodash/get';
import keys from 'lodash/keys';
import values from 'lodash/values';
import isEmpty from 'lodash/isEmpty';
import uniqueId from 'lodash/uniqueId';
import startCase from 'lodash/startCase';
import compact from 'lodash/compact';
import difference from 'lodash/difference';
import { formatDateString } from '../../utils';

const associatedPropertiesList = (productFilters, property) => {
  let productFilterList = productFilters.map(productFilter => {
    if (property === 'products') return `Token ID: #${productFilter}`;
    if (property === 'filters') return `Address: ${productFilter}`;
    return '';
  });
  productFilterList = productFilterList.join(', ');
  return productFilterList;
};

const associatedProperties = (item, property) => {
  const counter = 'associated-details-';
  const { oldData, newData } = item;
  const addedProductFilters = difference(newData[property], oldData[property]);
  const removedProductFilters = difference(oldData[property], newData[property]);
  let addedProductFilter;
  let removedProductFilter;
  if (addedProductFilters && addedProductFilters.length > 0) {
    addedProductFilter = {
      id: uniqueId(counter),
      message: `New ${startCase(property)} associated: ${associatedPropertiesList(
        addedProductFilters,
        property,
      )}`,
    };
  }

  if (removedProductFilters && removedProductFilters > 0) {
    removedProductFilter = {
      id: uniqueId(counter),
      message: `${startCase(property)} removed: ${associatedPropertiesList(
        removedProductFilters,
        property,
      )}`,
    };
  }

  const response = [addedProductFilter, removedProductFilter];
  return response;
};

// omit certain agreement details from contract activity
const detailsToBeOmitted = ['previousState', 'calculation', 'address'];

const detailsSectionCreator = item => {
  const counter = 'details-';
  const details = item.detailedDiff;
  const changeDetails = [];
  if (details) {
    const { added, updated, deleted } = details;
    const { oldData } = item;
    // add sanity check for `oldData` to prevent errors on corrupted data
    if (!isEmpty(updated) && !isEmpty(oldData)) {
      const updatedDetails = keys(updated)
        .filter(property => !detailsToBeOmitted.includes(property))
        .map(property => {
          if (property === 'products' || property === 'filters') {
            if (property === 'products') deleted.products = {};
            let response;
            if (updated[property]) {
              response = associatedProperties(item, property);
            }
            return response;
          }
          return {
            id: uniqueId(counter),
            message: `${startCase(property)} updated from ${
              property.includes('Date') ? formatDateString(oldData[property]) : oldData[property]
            } to ${
              property.includes('Date') ? formatDateString(updated[property]) : updated[property]
            }`,
          };
        });
      changeDetails.push(flatten(updatedDetails));
    }
    if (!isEmpty(added)) {
      const addedDetails = keys(added).map(property => {
        let productFilters;
        // if falsey
        if (!added[property]) {
          return false;
        }
        if (typeof added[property] === 'object') {
          const productFilterValues = values(added[property]);
          productFilters = associatedPropertiesList(productFilterValues, property);
        }
        return {
          id: uniqueId(counter),
          message: `New ${startCase(property)} associated: ${productFilters}`,
        };
      });
      changeDetails.push(addedDetails.filter(Boolean));
    }
    // add sanity check for `oldData` to prevent errors on corrupted data
    if (!isEmpty(deleted) && !isEmpty(oldData)) {
      const deletedDetails = keys(deleted).map(property => {
        let deletedValueString = get(oldData, property);
        if (typeof deleted[property] === 'object') {
          const productFilterKeys = keys(deleted[property]);
          const productFilterMessages = productFilterKeys.map(productFilter => {
            if (property === 'products') return ` Token ID: #${oldData.products[productFilter]}`;
            return oldData.filters[productFilter];
          });
          deletedValueString = productFilterMessages.join(',');
        }

        return property === 'products' && !deletedValueString
          ? ''
          : {
              id: uniqueId(counter),
              message: `${startCase(property)} removed${
                deletedValueString ? `: ${deletedValueString}` : ''
              }`,
            };
      });
      changeDetails.push(deletedDetails);
    }
  }
  return compact(flatten(changeDetails));
};

const ContractActivityDetails = ({ item, className }) => {
  const details = detailsSectionCreator(item);

  return details.length > 0 ? (
    <ul className={className}>
      {details.map(detail => (
        <Text as="li" variant="small" key={detail.id} block>
          {detail.message}
        </Text>
      ))}
    </ul>
  ) : (
    <></>
  );
};

ContractActivityDetails.propTypes = {
  item: PropTypes.shape({
    timestamp: PropTypes.string.isRequired,
    _id: PropTypes.string.isRequired,
    initiatedBy: PropTypes.shape({
      name: PropTypes.string.isRequired,
      email: PropTypes.string.isRequired,
    }),
    operation: PropTypes.string.isRequired,
    area: PropTypes.string.isRequired,
    newData: PropTypes.shape({
      address: PropTypes.string,
      name: PropTypes.string,
      rate: PropTypes.number,
      calculation: PropTypes.string,
      effectiveDate: PropTypes.string,
      terminationDate: PropTypes.string,
      platform: PropTypes.string,
      contractId: PropTypes.string,
    }),
    oldData: PropTypes.shape({
      name: PropTypes.string,
      rate: PropTypes.number,
      calculation: PropTypes.string,
      effectiveDate: PropTypes.string,
      terminationDate: PropTypes.string,
      platform: PropTypes.string,
    }),
    params: PropTypes.shape({
      contractAddress: PropTypes.string,
    }),
  }),
  className: PropTypes.string,
};

ContractActivityDetails.defaultProps = {
  item: {},
  className: '',
};
export default ContractActivityDetails;
